import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import styled, { css } from 'styled-components';

import Flex from '../Flex';
import { StyledButton } from '../Button';
import type { AsProp, WithAttributes } from '../../types';

type ResponsiveActionsProps = WithAttributes<
  'div',
  AsProp & {
    /* Ref assigned to the actions region. */
    actionsRef: RefObject<HTMLElement>;
    /* Actions region content. */
    actionsEl: ReactNode;
  }
>;

export const StyledResponsiveActionsContainer = styled.div<{ stacked?: boolean }>(
  ({ stacked }) => css`
    ${StyledButton} + ${StyledButton} {
      margin-inline-start: unset;
    }

    & > :not(a, button) {
      display: contents;
    }

    ${!stacked &&
    css`
      & > :is(a, button):first-child:not(:last-child),
      & > :first-child:not(:last-child) :is(a, button):first-child {
        margin-inline-end: auto;
      }
    `}

    &[aria-hidden='true'] {
      position: fixed;
      visibility: hidden;
    }
  `
);

const ResponsiveActions = ({ actionsRef, actionsEl, as, ...restProps }: ResponsiveActionsProps) => {
  const hiddenActionsRef = useRef<HTMLDivElement>(null);
  const [stacked, setStacked] = useState(false);

  useLayoutEffect(() => {
    if (hiddenActionsRef.current && actionsRef.current) {
      const minInlineWidth = hiddenActionsRef.current.getBoundingClientRect().width;

      const resizeObserver = new ResizeObserver(entries => {
        setTimeout(() => {
          if (stacked && entries[0].borderBoxSize[0].inlineSize > minInlineWidth) {
            setStacked(false);
          } else if (!stacked && entries[0].borderBoxSize[0].inlineSize <= minInlineWidth) {
            setStacked(true);
          }
        }, 0);
      });

      resizeObserver.observe(actionsRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  });

  useLayoutEffect(() => {
    hiddenActionsRef.current?.querySelectorAll('a, button').forEach(action => {
      if (action.hasAttribute('id')) {
        action.removeAttribute('id');
      }
      if (action.hasAttribute('data-testid')) {
        action.removeAttribute('data-testid');
      }
      action.setAttribute('role', 'none');
    });
  });

  return (
    <>
      <Flex
        {...restProps}
        as={StyledResponsiveActionsContainer}
        forwardedAs={as}
        ref={actionsRef}
        stacked={stacked}
        container={{
          gap: 1,
          ...(stacked ? { direction: 'column' } : { justify: 'end' })
        }}
      >
        {actionsEl}
      </Flex>
      <Flex
        as={StyledResponsiveActionsContainer}
        ref={hiddenActionsRef}
        aria-hidden
        inert=''
        container={{
          gap: 1,
          direction: 'row',
          justify: 'end'
        }}
      >
        {actionsEl}
      </Flex>
    </>
  );
};

export default ResponsiveActions;
